/**
 * Custom Hook für Firebase Authentication — wie Navigator:
 * Google zuerst per Popup (Session bleibt in der App-Origin),
 * Redirect nur als Fallback.
 */

import { useState, useEffect } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
} from 'firebase/auth';
import { auth } from '../firebase';
import { detectLanguage, getTranslations } from '../utils/i18n';
import {
  consumeAuthRedirectErrorCode,
  stashAuthRedirectErrorCode,
} from '../utils/authRedirectErrorStorage';

function isMobileUa() {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

function createGoogleProvider() {
  const provider = new GoogleAuthProvider();
  provider.addScope('email');
  provider.addScope('profile');
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
}

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    void getRedirectResult(auth)
      .then((result) => {
        if (cancelled || !result) return;
        setUser(result.user);
      })
      .catch((e) => {
        console.warn('Auth: getRedirectResult', e);
        const code =
          e && typeof e === 'object' && 'code' in e ? String(e.code) : '';
        if (code) stashAuthRedirectErrorCode(code);
      });

    const storedCode = consumeAuthRedirectErrorCode();
    if (storedCode) {
      const t = getTranslations(detectLanguage());
      setError(new Error(t.auth[storedCode] || storedCode));
    }

    const unsubscribe = onAuthStateChanged(
      auth,
      (next) => {
        if (cancelled) return;
        setUser(next);
        setLoading(false);
        if (next) setError(null);
      },
      (err) => {
        console.error('Auth state error:', err);
        if (!cancelled) {
          setError(err);
          setLoading(false);
        }
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const signInWithGoogle = async () => {
    setError(null);
    const googleProvider = createGoogleProvider();
    try {
      /* Popup behält die Session in der App-Origin (wie Navigator). */
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      const code = err?.code ? String(err.code) : '';
      const tryRedirect =
        code === 'auth/popup-blocked' ||
        code === 'auth/operation-not-supported-in-this-environment' ||
        code === 'auth/cancelled-popup-request' ||
        (isMobileUa() &&
          (code === 'auth/unauthorized-domain' ||
            code === 'auth/popup-closed-by-user'));

      if (tryRedirect) {
        try {
          console.log('Popup fehlgeschlagen, versuche Redirect…', code);
          await signInWithRedirect(auth, googleProvider);
          return;
        } catch (redirectErr) {
          console.error('Redirect auch fehlgeschlagen:', redirectErr);
          throw redirectErr;
        }
      }
      console.error('Google Popup Login Error:', err);
      throw err;
    }
  };

  const signInWithEmail = async (email, password) => {
    setError(null);
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signUpWithEmail = async (email, password) => {
    setError(null);
    await createUserWithEmailAndPassword(auth, email, password);
  };

  const logout = async () => {
    setError(null);
    await signOut(auth);
  };

  const getErrorMessage = (err) => {
    const t = getTranslations(detectLanguage());
    if (!err || !err.code) {
      return err?.message || t.auth.fallback;
    }
    return t.auth[err.code] || err.message || t.auth.fallback;
  };

  return {
    user,
    loading,
    error,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    logout,
    getErrorMessage,
  };
}
